import { NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { ORDER_PROCESSED_SSE_EVENTS } from '../../shared/constants/realtime.constants';
import { RealtimeEventsService } from '../../services/realtime-events.service';

export interface OrderProcessedRealtimeConfig {
  /**
   * Current location scope for this table. Must be a concrete location id —
   * empty string means "not ready"; events are dropped until a scope is set.
   */
  getLocationId: () => string;
  /** Selected lane filter id; empty string = "All Lanes". */
  getSelectedLaneId: () => string;
  /** Called inside `NgZone.run` when a matching order-processed event arrives. */
  onOrderProcessed: (data: any) => void;
}

/**
 * Bind the location-scoped `order_processed` SSE event from the shared stream so
 * the order table refreshes when an order is processed in the background.
 *
 * Contract:
 * - `GET /api/v1/events/stream` + Bearer JWT (user mode)
 * - Multiplexed stream (not channel-scoped) — we filter client-side by location.
 * - Location: a concrete table scope is required; incoming `location_id` must be
 *   present and equal to that scope (malformed / unscoped events are dropped).
 * - Lane filter: an order with no lane (`lane_id` null/omitted) belongs to ALL
 *   lanes, so it always passes; a lane-tagged order only passes when it matches
 *   the selected lane. Empty selection ("All Lanes") passes everything.
 *
 * Does not disconnect the stream — the caller only manages this Subscription.
 */
export function bindOrderProcessedSseEvent(
  realtime: RealtimeEventsService,
  ngZone: NgZone,
  config: OrderProcessedRealtimeConfig
): Subscription {
  const sub = new Subscription();

  // Ensure the user-authenticated stream is up (shared with payment SSE).
  if (!realtime.isConnected() || realtime.getAuthMode() !== 'user') {
    realtime.connectUser();
  }

  for (const eventName of ORDER_PROCESSED_SSE_EVENTS) {
    sub.add(
      realtime.on(eventName).subscribe((data) => {
        if (
          !orderProcessedEventMatchesFilters(
            data,
            config.getLocationId(),
            config.getSelectedLaneId()
          )
        ) {
          return;
        }
        ngZone.run(() => config.onOrderProcessed(data));
      })
    );
  }

  return sub;
}

/**
 * True when an `order_processed` payload should refresh the current table given
 * the active location scope and lane filter.
 *
 * Location: multiplexed SSE is not channel-scoped. Require a concrete table
 * scope and a matching incoming `location_id` — drop events while scope is
 * empty, and drop malformed events that omit location.
 *
 * Lane: a null/empty incoming lane means the order is not tied to a lane and must
 * show on every lane filter, so it always matches. A lane-tagged order matches
 * only its own lane. An empty selection ("All Lanes") matches everything.
 */
export function orderProcessedEventMatchesFilters(
  data: any,
  selectedLocationId: string,
  selectedLaneId: string
): boolean {
  if (!data || typeof data !== 'object') {
    return false;
  }

  const row = data as Record<string, any>;

  const incomingLoc = String(row['location_id'] ?? row['locationId'] ?? '').trim();
  const scopeLoc = String(selectedLocationId ?? '').trim();
  // No concrete scope yet (e.g. admin before locations load) → ignore.
  if (!scopeLoc) {
    return false;
  }
  // Scoped view requires a matching location_id on the event.
  if (!incomingLoc || incomingLoc !== scopeLoc) {
    return false;
  }

  const selectedLane = String(selectedLaneId ?? '').trim();
  if (!selectedLane) {
    return true;
  }

  const incomingLane = String(row['lane_id'] ?? row['laneId'] ?? '').trim();
  if (!incomingLane) {
    // No lane on the order → belongs to all lanes.
    return true;
  }
  return incomingLane === selectedLane;
}
